import React, { useState } from 'react';
import Modal from '@mui/material/Modal';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormGroup,
  FormHelperText,
  Input,
  InputLabel,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import beableAdminService from '../../../services/BeableAdminService';
import validationService from '../../../services/ValidationService';
import { BeableAdminValidation } from "../../../interfaces/AdminValidation";
import { getStyles } from '../styles';

function getModalStyle() {
    return {
        top: `26vh`,
        left: `34vw`,
    };
}

const defaultAdminValidation: BeableAdminValidation = {
    username: "",
    password: "",
    firstName: "",
    lastName: ""
}

async function validateProfile(profile: any) {
    const myValidation: BeableAdminValidation = JSON.parse(JSON.stringify(defaultAdminValidation));
    const upperCaseRegEx = /[A-Z]/gm
    const usernameBlacklisted = ['!', '#', '$', '%', '^', '&', '*', '(', ')', '_', '-', '+', '=', '{', '}', '[', ']', '<', '>', '?', '/', '|', ',', ' ', '\\',];

    let isInvalid = false;
    if (!profile.username) {
        myValidation.username = 'Username is required'; isInvalid = true
    } else {
        const firstChar = profile.username.charAt(0);
        const lastChar = profile.username.charAt(profile.username.length - 1);
        if (upperCaseRegEx.test(profile.username)) {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
        }
        if (usernameBlacklisted.some(v => profile.username.includes(v))) {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
        }
        if (profile.username.includes('..')) {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
        }
        if (profile.username.length < 4 || profile.username.length > 32) {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
        }
        if (firstChar === '@' || firstChar === '.') {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
        }
        if (lastChar === '@' || lastChar === '.') {
            myValidation.username = "Username must be [a-z,0-9,'@','.'].  Min: 4 Max: 32.  Begin and end with [a-z,0-9].";
        }
        if (myValidation.username.length > 0) {
            isInvalid = true;
        }
    }
    if (!profile.password) { myValidation.password = 'Password is required'; isInvalid = true }
    if (!profile.firstName) { myValidation.firstName = 'First Name is required'; isInvalid = true }
    if (!profile.lastName) { myValidation.lastName = 'Last Name is required'; isInvalid = true }
    if (profile.password) {
        const validation = await validationService.getPasswordValidationError(profile.password);
        if (validation) {
            myValidation.password = validation;
            isInvalid = true;
        }
    }
    return { myValidation, isInvalid };
}

interface AddBeableAdminModalProps {
    open: boolean;
    handleClose: () => void;
}
  
export default function AddBeableAdminModal({ open, handleClose }: AddBeableAdminModalProps) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [modalStyle] = useState(getModalStyle);
    const [adminUsername, setAdminUsername] = useState("");
    const [adminFirstName, setAdminFirstName] = useState("");
    const [adminLastName, setAdminLastName] = useState("");
    const [adminPassword, setAdminPassword] = useState("");
    const [validation, setValidation] = useState(defaultAdminValidation);
    const [isLoading, setLoading] = useState(false);
    const [hasErr, setErr] = useState(false);

    const resetState = () => {
        setAdminUsername("");
        setAdminPassword("");
        setValidation(defaultAdminValidation);
        setLoading(false);
        setErr(false);
    }

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            submitForm();
        }
    };

    const submitForm = async () => {
        const beableAdminProfile: any = {
            username: adminUsername,
            firstName: adminFirstName,
            lastName: adminLastName,
            password: adminPassword,
        }
        setErr(false);
        const validation = await validateProfile(beableAdminProfile);
        if (validation.isInvalid) { setValidation(validation.myValidation) }
        else {
            setValidation(defaultAdminValidation);
            setLoading(true);
            const beableAdmin = await beableAdminService.createBeableAdmin(beableAdminProfile);
            setLoading(false);

          if (beableAdmin) {
              resetState();
              handleClose();
              setErr(false);
            } else {
              setErr(true);
          }
        }
    };

    const handleAdminPassChange = (e: any) => {
        if (!e.target.value.trim().length) {
            setValidation(defaultAdminValidation);
        }
        setAdminPassword(e.target.value.trim());
    }
    const handleAdminUsernameChange = (e: any) => {
        if (!e.target.value.trim().length) {
            setValidation(defaultAdminValidation);
        }
        setAdminUsername(e.target.value.trim());
    }
    const handleAdminFirstNameChange = (e: any) => {
        if (!e.target.value.trim().length) {
            setValidation(defaultAdminValidation);
        }
        setAdminFirstName(e.target.value.trim());
    }
    const handleAdminLastNameChange = (e: any) => {
        if (!e.target.value.trim().length) {
            setValidation(defaultAdminValidation);
        }
        setAdminLastName(e.target.value.trim());
    }

    const passwordInputProps = {
        'data-testid': 'input-password',
    };

    return (
        <Modal
            aria-labelledby="admin-modal-title"
            aria-describedby="admin-modal-description"
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose();
                }
            }}
            onKeyPress={handleKeyPress}
        >
            <Box sx={[modalStyle, styles.paper]}>
                <h2 id="admin-modal-title">Create new beable admin</h2>
                <p id="admin-modal-description">Use the form below to add an beable admin.</p>
                <FormGroup>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="username">Username</InputLabel>
                        <Input onChange={handleAdminUsernameChange} id="username" aria-describedby="admin username" />
                        {validation.username !== '' ? <FormHelperText sx={styles.redTxt}>{validation.username}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="firstName">First Name</InputLabel>
                        <Input onChange={handleAdminFirstNameChange} id="firstName" aria-describedby="admin firstName" />
                        {validation.firstName !== '' ? <FormHelperText sx={styles.redTxt}>{validation.firstName}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="lastName">Last Name</InputLabel>
                        <Input onChange={handleAdminLastNameChange} id="lastName" aria-describedby="admin lastName" />
                        {validation.lastName !== '' ? <FormHelperText sx={styles.redTxt}>{validation.lastName}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard">
                        <InputLabel htmlFor="password">Password</InputLabel>
                        <Input 
                            onChange={handleAdminPassChange} 
                            id="password" 
                            aria-describedby="admin password"
                            type="password" 
                            inputProps={passwordInputProps}
                        />
                        {validation.password !== '' ? <FormHelperText sx={styles.redTxt}>{validation.password}</FormHelperText> : undefined}
                    </FormControl>
                </FormGroup>
                <FormGroup>
                    {isLoading ? <CircularProgress color="primary" /> : undefined}
                    {hasErr ? <Typography sx={styles.redTxt}>Username duplicated!</Typography> : undefined}
                </FormGroup>
                <FormGroup sx={[styles.mt2, styles.dfr]}>
                    <Button variant="contained" color="primary" onClick={submitForm}>Save admin</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            resetState();
                            handleClose();
                        }}
                        sx={styles.ml2}>Close</Button>
                </FormGroup>
            </Box>
        </Modal >
    );
}